import React, { ReactNode } from 'react'
import { TableCell } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { CartItemType, MaterialAvailabilityKey } from '@src/types/graphql-types'
import { ValidMaterialPricingDetailFragment } from '@src/fragments/ProductPricing.generated'
import HandleMarkup from '@src/components/HandleMarkup'
import MaterialTextWithCopy from '@src/components/MaterialTextWithCopy/MaterialTextWithCopy'
import MaterialPricingDisplay from '@src/components/MaterialPricingDisplay'
import MaterialAvailabilityPreview from '@src/components/MaterialAvailabilityPreview/MaterialAvailabilityPreview'

const useStyles = makeStyles<void, 'hidePriceMessageKey'>({
  name: 'ProductPriceAvailabilityRow',
  uniqId: 'ProductPAR01',
})((theme, _params, classes) => ({
  tableCell: {
    verticalAlign: 'middle',
    padding: theme.spacing(2, 3),
    [theme.breakpoints.between('sm', 'lg')]: {
      padding: theme.spacing(2, 1),
    },
  },
  materialNumber: {
    width: '20%',
    [`&.${classes.hidePriceMessageKey}`]: {
      width: '5%',
    },
  },
  materialNumberWithSku: {
    width: '30%',
  },
  packSize: {
    width: '15%',
    [`&.${classes.hidePriceMessageKey}`]: {
      width: '5%',
    },
  },
  packSizeWithSku: {
    width: '5%',
  },
  availability: {
    width: '45%',
    [`&.${classes.hidePriceMessageKey}`]: {
      width: '40%',
    },
  },
  price: {
    width: '10%',
  },
  listPrice: {
    display: 'none',
  },
  hidePriceMessageKey: {
    width: '20%',
  },
  labelFontSize: {
    fontSize: theme.typography.pxToRem(12),
  },
}))

interface ProductPriceAvailabilityRowProps {
  material: ValidMaterialPricingDetailFragment
  initialShipEstimate?: ReactNode
}

const ProductPriceAvailabilityRow: React.FC<
  ProductPriceAvailabilityRowProps
> = ({ material }) => {
  const { classes, cx } = useStyles()
  const isBBE = material?.type === CartItemType.ThirdPartyProvider
  const [availability] = material.availabilities ?? []
  const inquireForPAndA =
    availability?.key === MaterialAvailabilityKey.InquireForPriceAndAvailability

  return (
    <>
      <TableCell
        className={cx(classes.tableCell, classes.packSize, {
          [classes.packSizeWithSku]: isBBE,
          [classes.hidePriceMessageKey]: !!material?.hidePriceMessageKey,
        })}
      >
        <HandleMarkup value={material.packageSize} />
      </TableCell>
      <TableCell
        className={cx(classes.tableCell, classes.materialNumber, {
          [classes.materialNumberWithSku]: isBBE,
          [classes.hidePriceMessageKey]: !!material?.hidePriceMessageKey,
        })}
      >
        <MaterialTextWithCopy materialNumber={material.materialNumber} />
      </TableCell>
      <TableCell
        className={cx(classes.tableCell, classes.availability, {
          [classes.hidePriceMessageKey]: !!material?.hidePriceMessageKey,
        })}
      >
        <MaterialAvailabilityPreview
          material={material}
          canAddToCart
          hideDetails
        />
      </TableCell>
      <TableCell
        className={cx(classes.tableCell, classes.price, {
          [classes.hidePriceMessageKey]: !!material?.hidePriceMessageKey,
        })}
      >
        {!inquireForPAndA ? (
          <MaterialPricingDisplay
            material={material}
            priceClass={classes.price}
            listPriceClass={classes.listPrice}
          />
        ) : null}
      </TableCell>
    </>
  )
}

export default ProductPriceAvailabilityRow
